/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2026, Bulb Inc. For more information, please email contact@hellobulb.com.
 */

import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import type UppyT from '@uppy/core';
import { firstValueFrom, from, lastValueFrom } from 'rxjs';
import { mergeMap, toArray, map } from 'rxjs/operators';

import { TranslocoService } from '@jsverse/transloco';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';
import { TranslationService } from '../../common/translation.service';
import { AppConfigService } from '../../common/services/app-config/app-config.service';
import { BulbCKEStateService } from '../../ckeditor/editor/services/bulb-cke-state.service';
import { ImageType } from '../image-cropper/image-cropper.component';

/**
 * Width, in pixels, of the custom Canva image design.
 */
const CANVA_IMAGE_WIDTH = 1600;

/** Height, in pixels, of the custom Canva image design. */
const CANVA_IMAGE_HEIGHT = 1200;

export const CANVA_DIMENSION_PRESETS = {
  profile: { width: 145, height: 145 },
  coverImage: { width: 2280, height: 600 },
  page: { width: 1600, height: 1200 },
} as const;

export const CANVA_CAROUSEL_PRESETS: Record<string, { width: number, height: number }> = {
  [ImageType.LANDSCAPE]: { width: 1280, height: 720 }, // 16:9 aspect ratio
  [ImageType.PORTRAIT]: { width: 600, height: 800 }, // 3:4 aspect ratio
  [ImageType.SQUARE]: { width: 800, height: 800 }, // 1:1 aspect ratio
  [ImageType.FREEFORM]: { width: 1280, height: 720 }, // Default fallback for freeform
};

/** BULB endpoint that authenticates the user and opens a custom Canva design. */
const CANVA_EDITOR_URL = `/b/rest/canva/launch?width=${CANVA_IMAGE_WIDTH}&height=${CANVA_IMAGE_HEIGHT}`;

/** Stable window name used to reuse an editor popup that is already open. */
const CANVA_POPUP_NAME = 'bulb-canva-editor';

/** Browser features applied to the Canva editor popup. */
const CANVA_POPUP_FEATURES = 'popup,width=1200,height=850,resizable=yes,scrollbars=yes';

/** Maximum time that an editor popup may remain connected to this page. */
const CANVA_POPUP_TIMEOUT_MILLIS = 2 * 60 * 60 * 1000;

/** Maximum time to wait for Canva to finish an asynchronous export. */
const CANVA_EXPORT_TIMEOUT_MILLIS = 2 * 60 * 1000;

/** Delay before the first Canva export-status poll. */
const INITIAL_EXPORT_POLL_DELAY_MILLIS = 1000;

/** Upper bound for the export-status polling backoff. */
const MAX_EXPORT_POLL_DELAY_MILLIS = 5000;

/** Verified message returned by the BULB Canva popup callback page. */
type CanvaPopupMessage = {
  /** Identifies messages created by the BULB Canva callback. */
  source: 'bulb-canva';
  /** Indicates whether Canva returned a design or an error. */
  status: 'returned' | 'error';
  /** Server-managed transaction that authorizes the subsequent export. */
  transactionId?: string;
  /** Safe error message to show to the user. */
  message?: string;
};

/** Status returned by BULB while it manages an asynchronous Canva export. */
type CanvaExportStatus = {
  /** Server-managed transaction associated with the Canva design. */
  transactionId: string;
  /** Current export state. */
  status: 'RETURNED' | 'EXPORTING' | 'READY' | 'FAILED';
  /** Number of PNG pages available when the export is ready. */
  imageCount: number;
  /** Safe failure message supplied by the server. */
  message?: string;
};

/**
 * Owns the browser-side Canva workflow and adds completed Canva PNG files to an existing Uppy instance.
 */
@Injectable({
  providedIn: 'root',
})
export class CanvaUppyService {
  /** Canva editor popup currently associated with this browser page. */
  private activePopup: Window | null = null;

  /** Removes listeners and timers registered for the active popup. */
  private closePopupListener: (() => void) | null = null;

  /**
   * Creates the browser-side Canva integration service.
   *
   * @param appConfigService Runtime BULB configuration used to identify trusted callback origins
   * @param http Angular HTTP client used to call the server-managed export endpoints
   * @param translationService Provides localized Canva messages
   * @param translocoService
   * @param bulbCkeStateService
   * @param bulbErrorService Displays errors using the existing BULB error dialog
   * @param bulbBusyCoverService Displays the existing BULB busy cover during export and upload
   */
  constructor(
    private appConfigService: AppConfigService,
    private http: HttpClient,
    private translationService: TranslationService,
    private translocoService: TranslocoService,
    private bulbCkeStateService: BulbCKEStateService,
    @Inject('BulbErrorService') private bulbErrorService: any,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService,
  ) {
  }

  /**
   * Opens Canva and waits for the verified server callback. A second click focuses the current editor instead of
   * starting another OAuth/design flow.
   *
   * @param uppyInstance Uppy instance that receives and uploads the exported Canva pages
   * @param assetType
   * @param uploadState
   * @param exportFormat
   * @param orientation
   */
  public async openEditor(
    uppyInstance: UppyT,
    assetType: string,
    uploadState?: string,
    orientation?: string,
    exportFormat: 'png' | 'pptx' = 'png',
  ): Promise<void> {
    if (this.activePopup && !this.activePopup.closed) {
      this.activePopup.focus();
      return;
    }
    const carouselOrientation = uploadState === 'imageCarouselSlide' ? orientation : this.bulbCkeStateService.imageCarouselOrientation.getValue();
    let width: number;
    let height: number;

    if (uploadState && (uploadState === 'imageCarousel' || uploadState === 'imageCarouselSlide')) {
      this.bulbCkeStateService.imageCarouselOrientation.next('');
      ({ width, height } = CANVA_CAROUSEL_PRESETS[carouselOrientation]
        || CANVA_DIMENSION_PRESETS.page);
    } else {
      ({ width, height } = (uploadState && CANVA_DIMENSION_PRESETS[uploadState])
      || CANVA_DIMENSION_PRESETS.page);
    }

    // Construct the URL dynamically
    const editorUrl = `/b/rest/canva/launch?width=${width}&height=${height}`;

    this.closePopupListener?.();

    const popup = window.open(editorUrl, CANVA_POPUP_NAME, CANVA_POPUP_FEATURES);

    if (!popup) {
      this.showError(this.translationService.translateInstant('canva_popup_blocked'));
      return;
    }

    this.activePopup = popup;
    const allowedOrigins = this.getAllowedMessageOrigins();
    let closedWatcher: number | undefined;
    let popupTimeout: number | undefined;
    let handleMessage: (event: MessageEvent<unknown>) => Promise<void>;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      if (closedWatcher !== undefined) window.clearInterval(closedWatcher);
      if (popupTimeout !== undefined) window.clearTimeout(popupTimeout);
      if (this.activePopup === popup) this.activePopup = null;
      if (this.closePopupListener === cleanup) this.closePopupListener = null;
    };

    handleMessage = async (event: MessageEvent<unknown>) => {
      if (event.source !== popup
        || !allowedOrigins.has(event.origin)
        || !CanvaUppyService.isCanvaMessage(event.data)) return;

      cleanup();
      if (event.data.status !== 'returned' || !event.data.transactionId) {
        this.showError(event.data.message
          || this.translationService.translateInstant('canva_import_failed'));
        return;
      }

      try {
        await this.exportDesignToUppy(uppyInstance, event.data.transactionId, assetType, exportFormat);
      } catch (error) {
        this.showError(error.message);
      }
    };

    this.closePopupListener = cleanup;
    window.addEventListener('message', handleMessage);
    closedWatcher = window.setInterval(() => {
      if (popup.closed) cleanup();
    }, 1000);
    popupTimeout = window.setTimeout(cleanup, CANVA_POPUP_TIMEOUT_MILLIS);
  }

  /**
   * Starts and polls the server-managed export, adds each returned PNG to Uppy, and uploads the files.
   *
   * @param uppyInstance Uppy instance that receives the exported pages
   * @param transactionId Verified server transaction returned by the Canva callback
   */
  private async exportDesignToUppy(
    uppyInstance: UppyT,
    transactionId: string,
    assetType: string,
    exportFormat: 'png' | 'pptx',
  ): Promise<void> {
    const hideBusy = this.bulbBusyCoverService.showBusy();
    try {
      let exportStatus = await firstValueFrom(this.http.post<CanvaExportStatus>(
        'canva/exports',
        { transactionId, format: exportFormat },
      ));
      const deadline = Date.now() + CANVA_EXPORT_TIMEOUT_MILLIS;
      let pollDelay = INITIAL_EXPORT_POLL_DELAY_MILLIS;

      while (exportStatus.status !== 'READY') {
        if (exportStatus.status === 'FAILED') {
          throw new Error(exportStatus.message
            || this.translationService.translateInstant('canva_export_failed'));
        }
        if (Date.now() >= deadline) {
          throw new Error(this.translationService.translateInstant('canva_export_timeout'));
        }

        // Canva export jobs are asynchronous. Backoff reduces load while keeping quick exports responsive.
        // eslint-disable-next-line no-await-in-loop
        await CanvaUppyService.delay(Math.min(pollDelay, deadline - Date.now()));
        // eslint-disable-next-line no-await-in-loop
        exportStatus = await firstValueFrom(this.http.get<CanvaExportStatus>(
          `canva/exports/${encodeURIComponent(transactionId)}`,
        ));
        pollDelay = Math.min(Math.round(pollDelay * 1.5), MAX_EXPORT_POLL_DELAY_MILLIS);
      }

      const exportedAt = Date.now();

      if (exportFormat === 'pptx') {
        CanvaUppyService.allowCanvaPages(uppyInstance, 1);

        const fileBlob = await firstValueFrom(this.http.get(
          `canva/exports/${encodeURIComponent(transactionId)}/file`,
          { responseType: 'blob' },
        ));

        if (fileBlob.size === 0) {
          throw new Error(this.translationService.translateInstant('canva_import_failed'));
        }

        const imageFile = new File(
          [fileBlob],
          `canva_presentation_${exportedAt}.pptx`,
          { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' },
        );

        uppyInstance.addFile({
          name: imageFile.name,
          type: imageFile.type,
          data: imageFile,
          source: 'Canva',
          isRemote: false,
          meta: { canvaTransactionId: transactionId },
        });

      } else {
        if (!Number.isInteger(exportStatus.imageCount) || exportStatus.imageCount < 1) {
          throw new Error(this.translationService.translateInstant('canva_import_failed'));
        }

        if (assetType === 'attached_file' && exportStatus.imageCount > 1) {
          throw new Error(this.translocoService.translate('canva.formatInfo'));
        }

        CanvaUppyService.allowCanvaPages(uppyInstance, exportStatus.imageCount);
        const addedFileIds: string[] = [];
        const pages = Array.from({ length: exportStatus.imageCount }, (_, i) => i + 1);

        try {
          const downloadTasks$ = from(pages).pipe(
            mergeMap(async (pageNumber) => {
              const imageBlob = await firstValueFrom(this.http.get(
                `canva/exports/${encodeURIComponent(transactionId)}/pages/${pageNumber}`,
                { responseType: 'blob' },
              ));

              if (imageBlob.size === 0 || (imageBlob.type && imageBlob.type.indexOf('image/png') === -1)) {
                throw new Error(this.translationService.translateInstant('canva_import_failed'));
              }

              const pageSuffix = exportStatus.imageCount > 1 ? `_page_${pageNumber}` : '';
              const imageFile = new File(
                [imageBlob],
                `canva_image_${exportedAt}${pageSuffix}.png`,
                { type: 'image/png' },
              );

              return { imageFile, pageNumber };
            }, 3),
            toArray(),
            map((files) => files.sort((a, b) => a.pageNumber - b.pageNumber)),
          );

          const orderedFiles = await lastValueFrom(downloadTasks$);

          orderedFiles.forEach(({ imageFile, pageNumber }) => {
            const fileId = uppyInstance.addFile({
              name: imageFile.name,
              type: imageFile.type,
              data: imageFile,
              source: 'Canva',
              isRemote: false,
              meta: { canvaTransactionId: transactionId, canvaPageNumber: pageNumber },
            });
            addedFileIds.push(fileId);
          });
        } catch (error) {
          addedFileIds.forEach((fileId) => uppyInstance.removeFile(fileId));
          throw error;
        }
      }

      await uppyInstance.upload();
    } finally {
      hideBusy?.();
    }
  }

  /**
   * Expands the current Uppy file limit so all pages from one Canva design can be added together.
   *
   * @param uppyInstance Uppy instance receiving the files
   * @param imageCount Number of exported Canva pages
   */
  private static allowCanvaPages(uppyInstance: UppyT, imageCount: number): void {
    const requiredFileLimit = uppyInstance.getFiles().length + imageCount;
    uppyInstance.setOptions({
      restrictions: { maxNumberOfFiles: requiredFileLimit },
    });
  }

  /**
   * Gets the browser origins allowed to send the Canva completion message.
   *
   * @returns Current browser origin and the configured BULB server origin, when valid
   */
  private getAllowedMessageOrigins(): Set<string> {
    const origins = new Set([window.location.origin]);
    const configuredServerUrl = this.appConfigService.config?.base?.url;
    if (configuredServerUrl) {
      try {
        origins.add(new URL(configuredServerUrl, window.location.href).origin);
      } catch (error) {
        // Ignore malformed client config; the current browser origin remains trusted.
      }
    }
    return origins;
  }

  /**
   * Validates the shape and discriminator values of a Canva popup message.
   *
   * @param value Untrusted data received from `window.postMessage`
   * @returns Whether the value is a supported BULB Canva message
   */
  private static isCanvaMessage(value: unknown): value is CanvaPopupMessage {
    if (!value || typeof value !== 'object') return false;
    const message = value as Partial<CanvaPopupMessage>;
    return message.source === 'bulb-canva'
      && (message.status === 'returned' || message.status === 'error')
      && (message.transactionId === undefined || typeof message.transactionId === 'string')
      && (message.message === undefined || typeof message.message === 'string');
  }

  /**
   * Waits before the next export-status request.
   *
   * @param milliseconds Non-negative delay in milliseconds
   * @returns Promise that resolves after the delay
   */
  private static delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(resolve, Math.max(0, milliseconds));
    });
  }

  /**
   * Normalizes a Canva failure and displays it with BULB's standard error service.
   *
   * @param error Failure value to display
   */
  private showError(error: unknown): void {
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    this.bulbErrorService.showError(
      normalizedError.message.includes('exceeds maximum allowed size')
      || normalizedError.message.includes(this.translocoService.translate('canva.formatInfo'))
        ? normalizedError.message : null,
      normalizedError,
    );
  }
}
